package com.arena_web.data.websocket

import com.arena_web.core.logger.getLogger
import com.arena_web.data.http.HttpClientFactory
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import org.w3c.dom.CloseEvent
import org.w3c.dom.WebSocket
import org.w3c.dom.events.Event
import kotlin.math.min
import kotlin.random.Random

/** Backend WebSocket close codes (agreed contract). */
internal object WsCloseCode {
    /** Auth failed / access token expired — refresh once, then retry. */
    const val AUTH_FAILED = 4401

    /** Forbidden / not a member / resource missing — never reconnect. */
    const val FORBIDDEN = 4403
}

/**
 * One reconnecting WebSocket shared by the chat / user / event clients.
 *
 * - 4403: stop reconnecting to this resource.
 * - 4401: force one token refresh and retry; a second 4401 before the
 *   connection proved stable stops.
 * - anything else: capped exponential backoff with full-range jitter.
 * - The backoff (and the 4401 budget) resets only once the connection proved
 *   stable — first message received or [STABLE_AFTER_MS] open — not on mere
 *   open, so a server that accepts then immediately drops can't cause a
 *   tight reconnect loop.
 * - The access token is refreshed before (re)connecting only when it is
 *   expired / about to expire, not on every reconnect.
 */
internal class ManagedWebSocket(
    private val name: String,
    private val scope: CoroutineScope,
    private val buildUrl: (token: String) -> String,
    private val onText: (String) -> Unit,
    private val onConnected: () -> Unit,
    private val onDisconnected: () -> Unit,
) {
    private val logger = getLogger<ManagedWebSocket>()

    private var socket: WebSocket? = null
    /** Bumped on every start/stop so callbacks of a superseded socket are ignored. */
    private var generation = 0
    private var wanted = false
    private var forbidden = false
    private var attempts = 0
    private var authRefreshUsed = false
    private var stable = false
    private var reconnectTimerId: Int? = null
    private var stableTimerId: Int? = null
    private var connectJob: Job? = null

    /** True while a socket is open/connecting or a reconnect is pending. */
    val isBusy: Boolean
        get() {
            val state = socket?.readyState
            return state == WebSocket.CONNECTING || state == WebSocket.OPEN ||
                reconnectTimerId != null || connectJob?.isActive == true
        }

    fun start() {
        stop()
        wanted = true
        forbidden = false
        attempts = 0
        authRefreshUsed = false
        connectAsync(forceRefresh = false)
    }

    fun stop() {
        wanted = false
        generation++
        clearTimers()
        connectJob?.cancel()
        connectJob = null
        // Detach handlers BEFORE close: onclose fires asynchronously and must
        // not schedule a reconnect for a socket we no longer want.
        socket?.let { ws ->
            ws.onopen = null
            ws.onmessage = null
            ws.onerror = null
            ws.onclose = null
            ws.close()
        }
        socket = null
    }

    /**
     * Revive a socket that died (e.g. while the tab was hidden) with a fresh
     * retry budget. No-op when stopped, refused with 4403, or already alive.
     */
    fun ensureConnected() {
        if (!wanted || forbidden || isBusy) return
        attempts = 0
        authRefreshUsed = false
        connectAsync(forceRefresh = false)
    }

    fun send(text: String) {
        socket?.takeIf { it.readyState == WebSocket.OPEN }?.send(text)
    }

    private fun connectAsync(forceRefresh: Boolean) {
        val gen = generation
        connectJob = scope.launch {
            val stored = Storage.get(StorageKeys.ACCESS_TOKEN)
            val token =
                if (stored != null && (forceRefresh || isJwtExpired(stored))) {
                    // Single-flight refresh shared with the HTTP 401 path; on a
                    // network failure fall back to the stored token.
                    HttpClientFactory.ensureFreshToken(stored) ?: stored
                } else {
                    stored
                }
            if (gen != generation || !wanted) return@launch
            connectJob = null
            if (token == null) {
                logger.error("$name WS: no access token")
                return@launch
            }
            open(token, gen)
        }
    }

    private fun open(token: String, gen: Int) {
        val ws =
            try {
                WebSocket(buildUrl(token))
            } catch (e: Exception) {
                logger.error("$name WS: failed to create socket: ${e.message}")
                scheduleReconnect()
                return
            }
        stable = false

        ws.onopen = {
            if (gen == generation) {
                logger.debug("$name WS connected")
                onConnected()
                stableTimerId = window.setTimeout({ markStable() }, STABLE_AFTER_MS)
            }
            Unit
        }
        ws.onmessage = { event: Event ->
            if (gen == generation) {
                if (!stable) markStable()
                (event.asDynamic().data as? String)?.let(onText)
            }
            Unit
        }
        ws.onerror = {
            if (gen == generation) logger.error("$name WS error")
            Unit
        }
        ws.onclose = { event: Event ->
            if (gen == generation) {
                val code = event.unsafeCast<CloseEvent>().code.toInt()
                logger.debug("$name WS closed ($code)")
                stableTimerId?.let { window.clearTimeout(it) }
                stableTimerId = null
                socket = null
                onDisconnected()
                if (wanted) handleClose(code)
            }
            Unit
        }
        socket = ws
    }

    private fun handleClose(code: Int) {
        when (code) {
            WsCloseCode.FORBIDDEN -> {
                logger.debug("$name WS: forbidden (4403) — not reconnecting")
                forbidden = true
            }
            WsCloseCode.AUTH_FAILED -> {
                if (authRefreshUsed) {
                    logger.error("$name WS: auth failed again after refresh — giving up")
                } else {
                    authRefreshUsed = true
                    connectAsync(forceRefresh = true)
                }
            }
            else -> scheduleReconnect()
        }
    }

    private fun scheduleReconnect() {
        if (attempts >= MAX_ATTEMPTS) {
            logger.error("$name WS: max reconnect attempts reached")
            return
        }
        val cap = min(MAX_DELAY_MS, BASE_DELAY_MS shl min(attempts, 16))
        attempts++
        // Equal jitter: half fixed, half random — spreads a fleet of clients
        // reconnecting after a server restart.
        val delayMs = cap / 2 + Random.nextInt(cap / 2 + 1)
        logger.debug("$name WS reconnecting in ${delayMs}ms (attempt $attempts)")
        val gen = generation
        reconnectTimerId = window.setTimeout({
            reconnectTimerId = null
            if (gen == generation && wanted) connectAsync(forceRefresh = false)
        }, delayMs)
    }

    private fun markStable() {
        stable = true
        attempts = 0
        authRefreshUsed = false
        stableTimerId?.let { window.clearTimeout(it) }
        stableTimerId = null
    }

    private fun clearTimers() {
        reconnectTimerId?.let { window.clearTimeout(it) }
        reconnectTimerId = null
        stableTimerId?.let { window.clearTimeout(it) }
        stableTimerId = null
    }

    companion object {
        private const val BASE_DELAY_MS = 1_000
        private const val MAX_DELAY_MS = 30_000
        private const val MAX_ATTEMPTS = 10
        private const val STABLE_AFTER_MS = 30_000
    }
}

private val jwtJson = Json { ignoreUnknownKeys = true }

/**
 * True when the JWT's `exp` is in the past or within [skewSeconds]. A token
 * we can't decode is treated as not expired — the backend's 4401 is the
 * authoritative signal then.
 */
internal fun isJwtExpired(token: String, skewSeconds: Long = 30): Boolean {
    return try {
        val payload = token.split('.').getOrNull(1) ?: return false
        val b64 = payload.replace('-', '+').replace('_', '/')
            .let { it + "=".repeat((4 - it.length % 4) % 4) }
        val decoded = window.atob(b64)
        val exp = jwtJson.parseToJsonElement(decoded).jsonObject["exp"]?.jsonPrimitive?.longOrNull
            ?: return false
        val nowSeconds = (kotlin.js.Date.now() / 1000).toLong()
        exp - skewSeconds <= nowSeconds
    } catch (_: Throwable) {
        false
    }
}
